/**
 * Product Viewer Overlay
 *
 * Extends 360 viewer functionality with zoom and pan capabilities
 * - At 100% zoom: Drag anywhere on image to rotate (360 mode only)
 * - At 200%/300% zoom: Drag to pan, rotation disabled
 */

class ProductViewerOverlay extends HTMLElement {
  constructor() {
    super();

    this.mode = "VIEWER_360";
    this.zoomLevel = 100;
    this.currentIndex = 0;
    this.previousIndex = 0;
    this.loadedImages = new Set([0, 1, 2]);
    this.preloadTimeout = null;
    this.isDragging = false;

    this.panX = 0;
    this.panY = 0;
    this.startX = 0;
    this.startY = 0;
    this.startPanX = 0;
    this.startPanY = 0;

    this.startDragX = 0;
    this.startDragIndex = 0;

    this.container = null;
    this.images = [];
    this.zoomInBtn = null;
    this.zoomOutBtn = null;
    this.zoomLevelSpan = null;

    this.productData = null;
    this.imageCount = 0;

    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragMove = this.handleDragMove.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.handleZoomIn = this.handleZoomIn.bind(this);
    this.handleZoomOut = this.handleZoomOut.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.container = this.querySelector("[data-viewer-container]");
    this.zoomInBtn = this.querySelector("[data-zoom-in]");
    this.zoomOutBtn = this.querySelector("[data-zoom-out]");
    this.zoomLevelSpan = this.querySelector("[data-zoom-level]");

    if (!this.container) return;

    const dataScript = this.querySelector("[data-product-viewer-data]");
    if (!dataScript) {
      this.showError("No product data found");
      return;
    }

    try {
      this.productData = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error("Failed to parse product data:", e);
      this.showError("Failed to load product data");
      return;
    }

    if (
      this.productData.has360Images &&
      this.productData.images360.length > 0
    ) {
      this.mode = "VIEWER_360";
      this.imageCount = this.productData.images360.length;
      this.render360Images();
    } else if (
      this.productData.featuredImage &&
      this.productData.featuredImage.url
    ) {
      this.mode = "STATIC_IMAGE";
      this.imageCount = 1;
      this.renderStaticImage();
    } else {
      this.showError("No images available");
      return;
    }

    this.setupEventListeners();
    this.updateContainerState();

    if (this.mode === "VIEWER_360") {
      this.autoRotateOnOpen();
    }
  }

  autoRotateOnOpen() {
    // Use the start index calculated in render360Images
    const startIndex = this.initialStartIndex || 0;

    // Ensure we start at the correct position
    this.currentIndex = startIndex;
    this.previousIndex = startIndex;

    // Animate through 5 more images to show interactivity
    const totalSteps = 5;
    const delayBetweenSteps = 200; // ms between each step
    let currentStep = 0;

    const step = () => {
      if (currentStep >= totalSteps) {
        return; // Animation complete
      }

      // Calculate next image index going backward from start position
      const nextIndex = Math.max(startIndex - currentStep - 1, 0);

      // Update to next image
      this.currentIndex = nextIndex;
      this.updateImage(nextIndex);

      currentStep++;

      // Schedule next step
      setTimeout(step, delayBetweenSteps);
    };

    // Start after a brief delay to let the overlay fully open
    setTimeout(step, 300);
  }

  render360Images() {
    const fragment = document.createDocumentFragment();

    // Use current PDP slider position if available, otherwise fall back to startAngle
    const pdpSlider = document.querySelector("[data-viewer-slider]");
    let startIndex;
    if (pdpSlider) {
      startIndex = parseInt(pdpSlider.value, 10);
    } else {
      const startAngle = this.productData.startAngle || 0;
      startIndex = Math.round(
        (startAngle / 360) * (this.productData.images360.length - 1)
      );
    }
    this.initialStartIndex = startIndex;

    this.productData.images360.forEach((imageData, index) => {
      const img = document.createElement("img");
      img.classList.add("product-viewer-overlay__image");
      img.setAttribute("data-viewer-image", "");
      img.setAttribute("data-highres-src", imageData.urlHighRes);
      img.alt = imageData.alt || "";

      // Since auto-rotation goes backward 5 steps from start, preload those images
      const isNearStart = index >= startIndex - 5 && index <= startIndex;
      const shouldPreload = isNearStart || index < 3;

      if (shouldPreload) {
        img.src = imageData.url;
        this.loadedImages.add(index);
      } else {
        img.setAttribute("data-src", imageData.url);
      }

      // Set start image as active instead of image 0
      if (index === startIndex) {
        img.classList.add("is-active");
      }

      fragment.appendChild(img);
    });

    this.container.appendChild(fragment);
    this.images = Array.from(
      this.container.querySelectorAll("[data-viewer-image]")
    );

    // Preload surrounding images
    this.preloadSurroundingImages(0);
  }

  renderStaticImage() {
    const img = document.createElement("img");
    img.classList.add("product-viewer-overlay__image", "is-active");
    img.setAttribute("data-viewer-image", "");
    img.setAttribute(
      "data-highres-src",
      this.productData.featuredImage.urlHighRes
    );
    img.src = this.productData.featuredImage.url;
    img.alt = this.productData.featuredImage.alt || "";

    this.container.appendChild(img);
    this.images = [img];
    this.loadedImages.add(0);
  }

  showError(message) {
    this.container.innerHTML = `
      <div class="product-viewer-overlay__error">
        <p class="product-viewer-overlay__error-message">${message}</p>
      </div>
    `;
  }

  setupEventListeners() {
    if (this.zoomInBtn) {
      this.zoomInBtn.addEventListener("click", this.handleZoomIn);
    }
    if (this.zoomOutBtn) {
      this.zoomOutBtn.addEventListener("click", this.handleZoomOut);
    }

    this.container.addEventListener("mousedown", this.handleDragStart);
    this.container.addEventListener("touchstart", this.handleDragStart, {
      passive: false,
    });

    document.addEventListener("keydown", this.handleKeyDown);
  }

  cleanup() {
    document.removeEventListener("mousemove", this.handleDragMove);
    document.removeEventListener("touchmove", this.handleDragMove);
    document.removeEventListener("mouseup", this.handleDragEnd);
    document.removeEventListener("touchend", this.handleDragEnd);
    document.removeEventListener("keydown", this.handleKeyDown);

    if (this.preloadTimeout) {
      clearTimeout(this.preloadTimeout);
    }
  }

  handleKeyDown(e) {
    const overlay = this.closest(".overlay");
    if (!overlay || !overlay.classList.contains("active")) return;

    switch (e.key) {
      case "+":
      case "=":
        if (this.zoomLevel < 300) {
          this.handleZoomIn();
          e.preventDefault();
        }
        break;

      case "-":
      case "_":
        if (this.zoomLevel > 100) {
          this.handleZoomOut();
          e.preventDefault();
        }
        break;

      case "ArrowLeft":
        if (this.canRotate() && this.imageCount > 1) {
          this.currentIndex = this.currentIndex - 1 < 0 ? this.imageCount - 1 : this.currentIndex - 1;
          this.updateImage(this.currentIndex);
          e.preventDefault();
        }
        break;

      case "ArrowRight":
        if (this.canRotate() && this.imageCount > 1) {
          this.currentIndex = this.currentIndex + 1 >= this.imageCount ? 0 : this.currentIndex + 1;
          this.updateImage(this.currentIndex);
          e.preventDefault();
        }
        break;

      case "ArrowUp":
        if (this.canPan()) {
          this.panY -= 20;
          this.constrainPan();
          this.applyTransform();
          e.preventDefault();
        }
        break;

      case "ArrowDown":
        if (this.canPan()) {
          this.panY += 20;
          this.constrainPan();
          this.applyTransform();
          e.preventDefault();
        }
        break;
    }
  }

  handleZoomIn() {
    if (this.zoomLevel === 100) {
      this.zoomLevel = 200;
    } else if (this.zoomLevel === 200) {
      this.zoomLevel = 300;
    }
    this.onZoomChange();
  }

  handleZoomOut() {
    if (this.zoomLevel === 300) {
      this.zoomLevel = 200;
    } else if (this.zoomLevel === 200) {
      this.zoomLevel = 100;
    }
    this.onZoomChange();
  }

  onZoomChange() {
    if (this.zoomOutBtn) {
      this.zoomOutBtn.disabled = this.zoomLevel === 100;
    }
    if (this.zoomInBtn) {
      this.zoomInBtn.disabled = this.zoomLevel === 300;
    }
    if (this.zoomLevelSpan) {
      this.zoomLevelSpan.textContent = `${this.zoomLevel}%`;
    }

    // Reset pan when returning to 100%
    if (this.zoomLevel === 100) {
      this.panX = 0;
      this.panY = 0;
    }

    this.updateContainerState();
    this.applyTransform();

    if (this.zoomLevel >= 200) {
      this.loadHighResImage(this.currentIndex);
    }
  }

  updateContainerState() {
    this.container.classList.remove(
      "can-rotate",
      "is-rotating",
      "can-pan",
      "is-panning"
    );

    if (this.canRotate()) {
      this.container.classList.add("can-rotate");
    } else if (this.canPan()) {
      this.container.classList.add("can-pan");
    }
  }

  canRotate() {
    return this.mode === "VIEWER_360" && this.zoomLevel === 100;
  }

  canPan() {
    return this.zoomLevel > 100;
  }

  handleDragStart(e) {
    this.isDragging = true;

    const point = this.getPoint(e);
    this.startX = point.x;
    this.startY = point.y;
    this.startPanX = this.panX;
    this.startPanY = this.panY;
    this.startDragIndex = this.currentIndex;

    if (this.canRotate()) {
      this.container.classList.add("is-rotating");
      this.container.classList.remove("can-rotate");
    } else if (this.canPan()) {
      this.container.classList.add("is-panning");
      this.container.classList.remove("can-pan");
    }

    document.addEventListener("mousemove", this.handleDragMove);
    document.addEventListener("touchmove", this.handleDragMove, {
      passive: false,
    });
    document.addEventListener("mouseup", this.handleDragEnd);
    document.addEventListener("touchend", this.handleDragEnd);

    e.preventDefault();
  }

  handleDragMove(e) {
    if (!this.isDragging) return;

    const point = this.getPoint(e);
    const deltaX = point.x - this.startX;
    const deltaY = point.y - this.startY;

    if (this.canRotate()) {
      this.handleRotationDrag(deltaX);
    } else if (this.canPan()) {
      this.handlePanDrag(deltaX, deltaY);
    }

    e.preventDefault();
  }

  handleDragEnd() {
    this.isDragging = false;

    if (this.canRotate()) {
      this.container.classList.remove("is-rotating");
      this.container.classList.add("can-rotate");
    } else if (this.canPan()) {
      this.container.classList.remove("is-panning");
      this.container.classList.add("can-pan");
    }

    document.removeEventListener("mousemove", this.handleDragMove);
    document.removeEventListener("touchmove", this.handleDragMove);
    document.removeEventListener("mouseup", this.handleDragEnd);
    document.removeEventListener("touchend", this.handleDragEnd);
  }

  handleRotationDrag(deltaX) {
    const rect = this.container.getBoundingClientRect();
    const pixelsPerFullRotation = rect.width;
    const pixelsPerImage = pixelsPerFullRotation / this.imageCount;
    const indexChange = Math.round(deltaX / pixelsPerImage);

    let newIndex = this.startDragIndex + indexChange;

    // Wrap around
    while (newIndex < 0) newIndex += this.imageCount;
    while (newIndex >= this.imageCount) newIndex -= this.imageCount;

    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex;
      this.updateImage(this.currentIndex);
    }
  }

  handlePanDrag(deltaX, deltaY) {
    this.panX = this.startPanX + deltaX;
    this.panY = this.startPanY + deltaY;
    this.constrainPan();

    requestAnimationFrame(() => {
      this.applyTransform();
    });
  }

  constrainPan() {
    const scale = this.zoomLevel / 100;
    const containerRect = this.container.getBoundingClientRect();
    const activeImg = this.images[this.currentIndex];
    if (!activeImg) return;

    const imgRect = activeImg.getBoundingClientRect();
    const scaledWidth = imgRect.width * scale;
    const scaledHeight = imgRect.height * scale;
    const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
    const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

    this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
    this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
  }

  applyTransform() {
    const scale = this.zoomLevel / 100;

    this.images.forEach((img) => {
      img.style.transform = `scale(${scale}) translate(${
        this.panX / scale
      }px, ${this.panY / scale}px)`;
    });
  }

  loadHighResImage(index) {
    if (index < 0 || index >= this.imageCount) return;

    const img = this.images[index];
    if (!img) return;

    const highResSrc = img.getAttribute("data-highres-src");
    if (!highResSrc) return;

    // Check if already high-res
    if (img.src === highResSrc || img.getAttribute("data-src") === highResSrc)
      return;

    const preloader = new Image();
    preloader.onload = () => {
      img.src = highResSrc;
      img.removeAttribute("data-src");
    };
    preloader.src = highResSrc;
  }

  loadImage(index) {
    if (index < 0 || index >= this.imageCount) return;
    if (this.loadedImages.has(index)) return;

    const img = this.images[index];
    if (!img) return;

    const dataSrc = img.getAttribute("data-src");

    if (dataSrc) {
      const preloader = new Image();
      preloader.onload = () => {
        img.src = dataSrc;
        img.removeAttribute("data-src");
        this.loadedImages.add(index);

        // If this is the current index, show it now that it's loaded
        if (this.currentIndex === index) {
          this.updateImage(index);
        }
      };
      preloader.onerror = () => {
        this.loadedImages.add(index);
      };
      preloader.src = dataSrc;
    } else if (img.src) {
      this.loadedImages.add(index);
    }
  }

  preloadSurroundingImages(index) {
    if (this.preloadTimeout) clearTimeout(this.preloadTimeout);

    this.loadImage(index - 1);
    this.loadImage(index + 1);

    this.preloadTimeout = setTimeout(() => {
      for (let i = -5; i <= 5; i++) {
        this.loadImage(index + i);
      }
    }, 50);
  }

  updateImage(index) {
    if (index === this.previousIndex) return;
    if (!this.images[index]) return;

    const isLoaded = this.loadedImages.has(index);

    if (isLoaded) {
      this.images[index].classList.add("is-active");
      this.images[index].classList.remove("is-fading-out");

      if (this.previousIndex !== index && this.images[this.previousIndex]) {
        this.images[this.previousIndex].classList.remove("is-active");
        this.images[this.previousIndex].classList.add("is-fading-out");

        setTimeout(() => {
          if (this.images[this.previousIndex]) {
            this.images[this.previousIndex].classList.remove("is-fading-out");
          }
        }, 100);
      }

      this.previousIndex = index;
    } else {
      this.loadImage(index);
      return;
    }

    this.preloadSurroundingImages(index);
  }

  getPoint(e) {
    if (e.type.includes("touch")) {
      return {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
    return {
      x: e.clientX,
      y: e.clientY,
    };
  }
}

customElements.define("product-viewer-overlay", ProductViewerOverlay);
